import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Youtube } from "lucide-react";
import VituraAnimatedLogo from "@/components/VituraAnimatedLogo";
import { PrivacyLink } from "@/components/privacy/PrivacyLink";
import { MobileTermsLink } from "@/components/privacy/MobileTermsLink";
import { CookieSettingsLink } from "@/components/privacy/CookieSettingsLink";

/**
 * Layout for publicly-accessible content pages (/story, /journal, /journal/:slug)
 * that sit between the pre-launch early-access landing (/) and the private store (/preview).
 *
 * Keeps the aesthetic consistent with the early-access experience — no shop nav,
 * no cart, just a minimal header with a "Get early access" CTA back to /.
 */
export default function PublicLayout({ children }) {
  return (
    <div className="min-h-screen bg-[color:var(--bg)] text-[color:var(--ink)] flex flex-col" data-testid="public-layout">
      {/* Minimal header */}
      <header className="sticky top-0 z-40 border-b border-[color:var(--line)] bg-[color:var(--bg)]/80 backdrop-blur-xl">
        <div className="max-w-site mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <span className="label !text-[10px] hidden md:block">Pre-Launch · Vitura 001</span>
            <Link to="/ingredients" className="label !text-[10px] hover:opacity-60 transition" data-testid="public-nav-ingredients">Ingredients</Link>
            <Link to="/journal" className="label !text-[10px] hover:opacity-60 transition hidden sm:inline">Journal</Link>
          </div>
          <Link to="/" className="flex items-center gap-2" data-testid="public-logo">
            <VituraAnimatedLogo color="#0f0f10" width={140} />
          </Link>
          <Link
            to="/"
            className="h-9 px-4 bg-[#0f0f10] text-[#faf9f5] label !tracking-[0.22em] hover:bg-[#2a2a2a] transition inline-flex items-center"
            data-testid="public-ea-cta"
          >
            Get early access
          </Link>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      {/* Minimal footer */}
      <footer className="bg-[#0f0f10] text-[#faf9f5] border-t border-[#faf9f5]/10 mt-auto" data-testid="public-footer">
        <div className="max-w-site mx-auto px-6 md:px-10 py-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="mb-5"><VituraAnimatedLogo color="#faf9f5" width={120} /></div>
              <p className="text-[13px] text-[#faf9f5]/70 max-w-sm leading-relaxed">
                The daily performance shot for high performers chasing their edge. Smooth energy, calm focus, stress resilience and recovery — in one 2oz shot.
              </p>
            </div>
            <div className="md:col-span-3">
              <div className="label !text-[10px] !text-[#faf9f5]/60 mb-4">Read</div>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/ingredients" className="hover:opacity-70">Ingredients</Link></li>
                <li><Link to="/journal" className="hover:opacity-70">Journal</Link></li>
                <li><Link to="/journal/clinical-doses-matter" className="hover:opacity-70">The Science</Link></li>
              </ul>
            </div>
            <div className="md:col-span-2">
              <div className="label !text-[10px] !text-[#faf9f5]/60 mb-4">Contact</div>
              <ul className="space-y-2.5 text-sm">
                <li><a href="mailto:hello@takevitura.com" className="hover:opacity-70">hello@takevitura.com</a></li>
                <li><PrivacyLink testId="public-footer-privacy-link" /></li>
                <li><MobileTermsLink testId="public-footer-mobile-terms-link" /></li>
                <li><CookieSettingsLink testId="public-footer-cookie-settings-link" /></li>
              </ul>
            </div>
            <div className="md:col-span-2">
              <div className="label !text-[10px] !text-[#faf9f5]/60 mb-4">Follow</div>
              <ul className="space-y-2.5 text-sm">
                <li><a href="https://instagram.com/takevitura" target="_blank" rel="noopener noreferrer" className="hover:opacity-70 inline-flex items-center gap-2"><Instagram size={14} /> @takevitura</a></li>
                <li><a href="https://x.com/takevitura" target="_blank" rel="noopener noreferrer" className="hover:opacity-70 inline-flex items-center gap-2"><Twitter size={14} /> @takevitura</a></li>
                <li><a href="https://youtube.com/@takevitura?si=_jxEP3uLqmncA142" target="_blank" rel="noopener noreferrer" className="hover:opacity-70 inline-flex items-center gap-2" data-testid="public-youtube"><Youtube size={14} /> @takevitura</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-[#faf9f5]/10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="label !text-[10px] !text-[#faf9f5]/50">© {new Date().getFullYear()} VITURA · Engineered in the USA</div>
            <Link to="/" className="label !text-[#faf9f5] underline">Reserve early access →</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
